<template>
  <div v-if="newsletter.status === 'dormant'" class="card">
    {{ t("web.subscription.message.dormant_notice") }}
  </div>
  <div v-else-if="newsletter.should_be_private" class="card">
    {{ t("web.subscription.message.private_notice") }}
  </div>
  <div
    v-else-if="
      (newsletter.should_disable_non_premium_subscriptions ||
        variant === 'paywalled_archive_page') &&
      !subscriber
    "
    class="card"
  >
    <PaymentElement
      v-if="IMMEDIATELY_SHOW_PAYMENT_ELEMENT"
      :newsletter="newsletter"
      :email-address="emailAddress"
    />
    <div
      v-else-if="
        purchaseUrl && !showEmailInput && magicLinkStatus !== 'success'
      "
    >
      <action-button
        :text="
          newsletter.subscription_button_text ||
          (newsletter.paid_subscription_free_trial_duration
            ? 'Subscribe and start a free trial'
            : 'Subscribe')
        "
        color="blue"
        :href="purchaseUrl"
      />
    </div>
    <div
      v-if="!emailAddress || magicLinkStatus === 'success'"
      class="magic-link-container"
    >
      <div
        v-if="magicLinkStatus === 'success'"
        class="magic-link-success-message"
      >
        Check your email for a link to log in. This page will reload
        automatically once you click the link.
      </div>
      <div
        v-else-if="magicLinkStatus === 'error'"
        class="magic-link-error-message"
      >
        {{ magicLinkMessage }}
      </div>
      <div v-else class="magic-link-form">
        <label v-if="showEmailInput" for="magic-link-email" class="sr-only">
          Email address for sign-in link
        </label>
        <input
          v-if="showEmailInput"
          id="magic-link-email"
          ref="magicEmailInput"
          v-model="localEmailAddress"
          type="email"
          placeholder="Your email address"
          class="magic-link-input"
          :disabled="magicLinkStatus === 'pending'"
          @keydown.enter="
            (event) => {
              event.preventDefault();
              if (localEmailAddress && magicLinkStatus !== 'pending') {
                sendMagicLink();
              }
            }
          "
        />
        <Turnstile
          v-if="showEmailInput && magicLinkCaptchaRequired && !isOnCustomDomain"
          @callback="
            (token) => {
              magicLinkCaptchaToken = token;
              sendMagicLink();
            }
          "
          @expired-callback="magicLinkCaptchaToken = ''"
          @error-callback="
            () => {
              magicLinkMessage =
                'CAPTCHA verification failed. Please try again.';
              magicLinkCaptchaRequired = false;
              magicLinkCaptchaToken = '';
            }
          "
        />
        <action-button
          v-if="showEmailInput && !magicLinkCaptchaRequired"
          :text="
            magicLinkStatus === 'pending'
              ? 'Sending sign-in link...'
              : 'Send sign-in link'
          "
          color="blue"
          :disabled="
            magicLinkStatus === 'pending' ||
            !localEmailAddress ||
            (magicLinkCaptchaRequired && !magicLinkCaptchaToken)
          "
          :pending="magicLinkStatus === 'pending'"
          class="magic-link-button"
          @click="handleMagicLinkClick"
        />
        <button
          v-else
          type="button"
          class="magic-link-toggle-button"
          @click="handleMagicLinkClick"
        >
          <div class="magic-link-toggle-text">
            Already a paid subscriber? Click here to log in.
          </div>
        </button>
      </div>
    </div>
  </div>
  <div v-else class="card">
    <div class="archive__subscribe">
      <Turnstile
        v-if="
          state === 'invalid_ip_address' &&
          !turnstileResponseToken &&
          !isOnCustomDomain
        "
        @callback="emit('update:turnstileResponseToken', $event)"
      />
      <form
        v-else
        class="archive__subscribe-form"
        @submit.prevent="() => disabled || subscribe()"
      >
        <p
          v-if="newsletter.subscription_form_text"
          class="heading archive__subscribe-form-heading"
        >
          {{ newsletter.subscription_form_text }}
        </p>

        <input
          type="submit"
          :value="
            newsletter.subscription_button_text || t('web.button.subscribe')
          "
          class="hidden-submit"
        />

        <div
          v-if="validationAnnouncement"
          role="alert"
          aria-live="assertive"
          class="sr-only"
        >
          {{ validationAnnouncement }}
        </div>

        <template
          v-if="
            state !== 'pending_subscription' &&
            state !== 'unactivated' &&
            !subscriber
          "
        >
          <template
            v-for="field in newsletter.autonomy_fields"
            :key="field.key"
          >
            <label
              v-if="field.config.type === 'checkbox'"
              :for="`sfi-${field.key}`"
              class="archive__subscribe-form-label"
            >
              <input
                :id="`sfi-${field.key}`"
                type="checkbox"
                :checked="!!metadata[field.key]"
                @change="(event) => handleMetadataInput(field.key, event)"
              />
              <span>{{ field.label }}</span>
            </label>

            <template v-else-if="field.config.type === 'text'">
              <label
                :for="`sfi-${field.key}`"
                class="archive__subscribe-form-label sr-only"
              >
                {{ placeholder(field) }}
              </label>

              <input
                :id="`sfi-${field.key}`"
                :value="metadata[field.key]"
                type="text"
                :required="field.config.required"
                :placeholder="placeholder(field)"
                :autocomplete="AUTOCOMPLETE_ATTRIBUTES[field.key] || 'off'"
                @input="(event) => handleMetadataInput(field.key, event)"
              />
            </template>

            <template v-else-if="field.config.type === 'select'">
              <label
                :for="`sfi-${field.key}`"
                class="archive__subscribe-form-label sr-only"
              >
                {{ placeholder(field) }}
              </label>

              <select
                :id="`sfi-${field.key}`"
                :value="metadata[field.key]"
                :required="field.config.required"
                @input="(event) => handleMetadataInput(field.key, event)"
              >
                <option value="" :disabled="field.config.required" selected>
                  {{ placeholder(field) }}
                </option>
                <option
                  v-for="(option, i) in field.config.options"
                  :key="i"
                  :value="option.id"
                >
                  {{ option.label }}
                </option>
              </select>
            </template>
          </template>
        </template>
        <label for="sfi-email" class="archive__subscribe-form-label sr-only">
          {{ t("web.form.placeholder.email_with_example") }}
        </label>

        <input
          v-if="
            state !== 'unactivated' &&
            !subscriber &&
            state !== 'subscribed_elsewhere'
          "
          id="sfi-email"
          class="archive__subscribe-form-input archive__input"
          required
          :value="emailAddress"
          type="email"
          :placeholder="t('web.form.placeholder.email_with_example')"
          :readonly="state === 'pending_subscription'"
          autocomplete="email"
          @input="handleInput"
        />
        <span v-if="state === 'unpaid'">
          To finish subscribing to <strong>{{ newsletter.name }}</strong
          >, click below to upgrade your subscription.
        </span>
        <span
          v-if="
            state === 'regular' &&
            newsletter.paid_subscriptions_status !== 'active'
          "
          class="confirmation-email-message"
          v-html="subscribedViaMessage"
        >
        </span>
        <span
          v-if="
            state === 'regular' &&
            newsletter.paid_subscriptions_status === 'active'
          "
        >
          You're subscribed to <strong>{{ newsletter.name }}</strong
          >, but can upgrade to a paid subscription if you'd like!
        </span>
        <span v-else-if="state in STATE_TO_LABEL">
          {{
            STATE_TO_LABEL[state].replace("{newsletter.name}", newsletter.name)
          }}
        </span>

        <action-button
          v-if="state === 'regular' && manageSubscriptionUrl"
          :text="t('web.subscription.action.manage')"
          color="blue"
          :href="manageSubscriptionUrl"
        />
        <action-button
          v-if="state === 'pending_validation'"
          text="Validating..."
          color="blue"
          pending
        />
        <action-button
          v-if="state === 'pending_subscription'"
          :text="
            newsletter.subscription_button_text ||
            (newsletter.paid_subscription_free_trial_duration
              ? t('web.subscription.action.subscribe_free_trial')
              : t('web.button.subscribe'))
          "
          color="blue"
          pending
          @click="() => disabled || subscribe()"
        />
        <action-button
          v-if="
            state === 'not_subscribed' ||
            (state === 'invalid_ip_address' && turnstileResponseToken)
          "
          :text="
            newsletter.subscription_button_text ||
            (newsletter.paid_subscription_free_trial_duration
              ? t('web.subscription.action.subscribe_free_trial_days').replace(
                  '{{ newsletter.paid_subscription_free_trial_duration }}',
                  String(newsletter.paid_subscription_free_trial_duration)
                )
              : variant === 'paywalled_archive_page'
              ? t('web.button.log_in')
              : t('web.button.subscribe'))
          "
          color="blue"
          :disabled="disabled"
          @click="() => (disabled ? highlightRequiredFields() : subscribe())"
        />
        <action-button
          v-if="state === 'subscribed_elsewhere'"
          :text="`Subscribe as ${emailAddress}`"
          color="blue"
          @click="() => subscribe()"
        />
        <action-button
          v-if="state === 'unsubscribed'"
          :text="t('web.button.resubscribe')"
          color="yellow"
          @click="() => subscriber && resubscribe(subscriber.id)"
        />
        <action-button
          v-if="state === 'invalid_email__regex'"
          :text="t('web.form.validation.email_malformed')"
          color="red"
          disabled
        />
        <action-button
          v-if="state === 'invalid_subscription'"
          :text="t('web.form.validation.email_didnt_work')"
          color="red"
          disabled
        />
        <div v-if="state === 'unactivated'" class="unactivated-section">
          <div class="confirmation-email-message">
            {{ t("web.subscription.message.confirmation_email_sent") }}
            <strong>{{ emailAddress }}</strong
            >.
          </div>
          <a
            v-if="sniperLinkData"
            :href="sniperLinkData.url"
            target="_blank"
            rel="noopener noreferrer"
            type="button"
            class="archive__button archive__subscribe-form-button tinted blue sniper-link-button"
          >
            <img
              v-if="sniperLinkData.image"
              :src="sniperLinkData.image"
              :alt="sniperLinkData.provider_pretty"
              class="sniper-link-image"
            />
            <div class="sniper-link-text">
              {{ t("web.action.take_me_to") }}
              {{ sniperLinkData.provider_pretty }}
            </div>
          </a>
        </div>
        <action-button
          v-if="state === 'trialed' && upgradeUrl"
          :href="upgradeUrl"
          :text="t('web.button.upgrade_subscription')"
          color="yellow"
        />

        <action-button
          v-if="
            (state === 'unpaid' ||
              (state === 'regular' &&
                newsletter.paid_subscriptions_status === 'active')) &&
            upgradeUrl
          "
          :href="upgradeUrl"
          :text="t(upgradeText)"
          color="blue"
        />
        <action-button
          v-if="
            ['past_due', 'churning', 'churned', 'paused', 'premium'].includes(
              state
            ) && manageSubscriptionUrl
          "
          :href="manageSubscriptionUrl"
          :text="t('web.subscription.action.manage')"
          color="blue"
        />
        <action-button
          v-if="state === 'churning' && archiveUrl"
          :href="archiveUrl"
          :text="t('web.button.view_archives')"
          color="yellow"
        />
        <action-button
          v-if="
            (state === 'premium' ||
              state === 'gifted' ||
              state === 'churning') &&
            archiveUrl &&
            variant === 'subscribe_page'
          "
          :href="archiveUrl"
          :text="t('web.button.view_archives')"
          color="blue"
        />
        <action-button
          v-if="state === 'errored_email'"
          :text="error || t('web.message.error.generic')"
          color="red"
        />
        <action-button
          v-if="state === 'awaiting_email_address_confirmation'"
          :text="t('web.subscription.action.email_confirmed')"
          color="red"
          @click="checkSubscriber"
        />
        <action-button
          v-if="
            state === 'undeliverable' ||
            state === 'pending_email_address_confirmation'
          "
          :href="`mailto:${newsletter.email_address}`"
          :text="t('web.action.contact_author')"
          color="red"
        />
      </form>
    </div>
  </div>
  <Teleport to="body">
    <div
      v-if="showConfirmedDialog"
      class="subscription-success-overlay"
      @click="closeConfirmedDialog"
    >
      <div class="subscription-success-dialog" @click.stop>
        <div class="subscription-success-header">
          <button
            type="button"
            class="subscription-success-close"
            aria-label="Close"
            @click="closeConfirmedDialog"
          >
            <CloseIcon class="subscription-success-close-icon" />
          </button>
        </div>
        <div class="subscription-success-content">
          <p
            v-if="
              newsletter.paid_subscriptions_status === 'active' && upgradeUrl
            "
            class="subscription-success-message"
          >
            {{ t("web.subscription.message.subscribed_to") }}
            <strong>{{ newsletter.name }}</strong
            >!
            {{ t("web.subscription.message.support_by_upgrading") }}
          </p>
          <p v-else class="subscription-success-message">
            {{ t("web.subscription.message.subscribed_to") }}
            <strong>{{ newsletter.name }}</strong
            >!
          </p>
        </div>
        <div class="subscription-success-footer">
          <a
            v-if="
              newsletter.paid_subscriptions_status === 'active' && upgradeUrl
            "
            :href="upgradeUrl"
            class="subscription-success-button"
          >
            {{ t("web.button.upgrade_subscription") }}
          </a>
          <button
            v-else
            type="button"
            class="subscription-success-button"
            @click="closeConfirmedDialog"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

import Urls from "@/autogen/urls";
import CloseIcon from "@/icons/heroicons/x-mark-micro.svg";
import type { Newsletter } from "@/types/newsletter";

import { type TranslationKey, createTranslator } from "../lib/utils";
import ActionButton from "./Button.vue";
import {
  type BasicSubscriber,
  type SubscribeFormVariant,
  type SubscriptionState,
  mutableUrl,
} from "./lib";
import Turnstile from "./Turnstile.vue";

// Lazy load PaymentElement to avoid loading Stripe JS for free newsletters
const PaymentElement = defineAsyncComponent(
  () => import("./PaymentElement.vue")
);

interface Props {
  newsletter: Newsletter;
  subscriber?: BasicSubscriber;
  state: SubscriptionState;
  variant: SubscribeFormVariant;
  emailAddress: string;
  metadata: { [key: string]: string };
  checkSubscriber: (payload: MouseEvent) => void;
  subscribe: () => void;
  resubscribe: (subscriber_id: string) => void;
  error?: string;
  turnstileResponseToken: string;
}

type MagicLinkStatus = "idle" | "pending" | "success" | "error";

const magicLinkStatus = ref<MagicLinkStatus>("idle");
const magicLinkMessage = ref("");
const showEmailInput = ref(false);
const localEmailAddress = ref("");
const magicLinkPollingInterval = ref<ReturnType<typeof setInterval> | null>(
  null
);
const magicEmailInput = ref<HTMLInputElement | null>(null);
const magicLinkCaptchaRequired = ref(false);
const magicLinkCaptchaToken = ref("");
const showConfirmedDialog = ref(false);
const validationAnnouncement = ref("");

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:emailAddress": [value: string];
  "update:metadata": [value: { [key: string]: string }];
  "update:turnstileResponseToken": [value: string];
}>();

const senderEmail = computed(() => {
  return props.newsletter.email_domain
    ? props.newsletter.email_address
    : `${props.newsletter.username}@buttondown.email`;
});

const sniperLinkQuery = useQuery({
  queryKey: computed(() => [
    "sniper-link",
    props.emailAddress,
    senderEmail.value,
  ]),
  enabled: computed(
    () => props.state === "unactivated" && !!props.emailAddress
  ),
  async queryFn({ signal }): Promise<{
    url: string;
    image: string;
    provider_pretty: string;
  } | null> {
    const response = await fetch(
      `https://sniperl.ink/v1/render?recipient=${encodeURIComponent(
        props.emailAddress
      )}&sender=${encodeURIComponent(senderEmail.value)}`,
      { signal }
    );
    if (response.ok) {
      return await response.json();
    }
    // 404 is expected when email provider can't be determined - return null to hide button
    if (response.status === 404) {
      return null;
    }
    // For other errors, throw to let TanStack Query handle it
    throw new Error(`Failed to fetch sniper link: ${response.status}`);
  },
  retry: false,
});

const sniperLinkData = computed(() => sniperLinkQuery.data.value);

const upgradeText = computed<TranslationKey>(() => {
  if (props.variant === "subscribe_page") {
    return "web.button.upgrade_subscription";
  }
  if (props.variant === "archive_page") {
    return "web.button.read_rest";
  }
  if (props.variant === "paywalled_archive_page") {
    return "web.button.log_in";
  }
  return "web.button.upgrade_subscription";
});

// Simple check for obviously invalid emails (missing @ or domain)
const looksLikeEmail = (email: string): boolean => {
  if (!email) return false;
  const atIndex = email.indexOf("@");
  // Must have @ with something before and after it
  return atIndex > 0 && atIndex < email.length - 1;
};

const disabled = computed(() => {
  // Make sure all required fields (incl. the email itself) are present
  // and that the email at least looks like an email address.
  return (
    !props.emailAddress ||
    !looksLikeEmail(props.emailAddress) ||
    (props.newsletter.autonomy_fields || [])
      .filter((field) => "required" in field.config && field.config.required)
      .some((field) => !props.metadata[field.key])
  );
});

const purchaseUrl = computed(() => {
  return `https://buttondown.com/${props.newsletter.username}/buy`;
});

const upgradeUrl = computed(() => {
  return mutableUrl(props.newsletter, props.subscriber, "/upgrade/monthly");
});

const manageSubscriptionUrl = computed(() => {
  if (!props.newsletter.enabled_features.includes("portal")) {
    return null;
  }
  if (!props.subscriber) {
    return null;
  }
  return `https://buttondown.com/${props.newsletter.username}/management/subscribers/${props.subscriber?.id}`;
});

const archiveUrl = computed(() => {
  if (!props.newsletter.enabled_features.includes("archives")) {
    return null;
  }
  return mutableUrl(props.newsletter, props.subscriber, "/archive");
});

const handleInput = (event: Event) => {
  const element = event.target as HTMLInputElement;
  emit("update:emailAddress", element.value);
};

const handleMetadataInput = (key: string, event: Event) => {
  const element = event.target as HTMLInputElement;
  const value =
    element.type === "checkbox" ? (element.checked ? "on" : "") : element.value;
  emit("update:metadata", {
    ...props.metadata,
    [key]: value,
  });
};

const placeholder = (field: any): string => {
  return `${field.label}${field.config.required ? "*" : ""}`;
};

const t = createTranslator(() => props.newsletter.locale);

const subscribedViaMessage = computed(() => {
  const message = t("web.subscription.message.subscribed_via");
  // Escape HTML entities in user-provided values to prevent XSS
  const escapeHtml = (str: string) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  return message
    .replace(
      "{newsletter}",
      `<strong>${escapeHtml(props.newsletter.name)}</strong>`
    )
    .replace("{email}", `<strong>${escapeHtml(props.emailAddress)}</strong>`);
});

const highlightRequiredFields = () => {
  if (!disabled.value) {
    return;
  }
  const requiredFields = props.newsletter.autonomy_fields
    .filter((field) => "required" in field.config && field.config.required)
    .filter((field) => !props.metadata[field.key]);

  const fieldNames = requiredFields.map((field) => field.label).join(", ");
  validationAnnouncement.value = `Please fill in required fields: ${fieldNames}`;

  requiredFields.forEach((field) => {
    const element = document.getElementById(`sfi-${field.key}`);
    if (element) {
      element.classList.add("required");
      element.setAttribute("aria-invalid", "true");
    }
  });
  setTimeout(() => {
    validationAnnouncement.value = "";
    requiredFields.forEach((field) => {
      const element = document.getElementById(`sfi-${field.key}`);
      if (element) {
        element.classList.remove("required");
        element.removeAttribute("aria-invalid");
      }
    });
  }, 2000);
};

const IMMEDIATELY_SHOW_PAYMENT_ELEMENT =
  (props.newsletter as any).should_immediately_show_payment_element ||
  window.location.href.includes("immediately_show_payment_element");

const handleMagicLinkClick = () => {
  // If emailAddress is already set, we don't need sign-in link
  if (props.emailAddress) {
    return;
  }
  // Otherwise, show the input field
  if (!showEmailInput.value) {
    showEmailInput.value = true;
  } else {
    // Input is shown, send the sign-in link
    sendMagicLink();
  }
};

const sendMagicLink = async () => {
  const emailToUse = localEmailAddress.value;
  if (!emailToUse) {
    return;
  }

  magicLinkStatus.value = "pending";
  magicLinkMessage.value = "";

  try {
    const formData = new FormData();
    formData.append("email", emailToUse);
    // Include return_url so sign-in link redirects back to current page
    formData.append("return_url", window.location.href);
    if (magicLinkCaptchaToken.value) {
      formData.append("cf-turnstile-response", magicLinkCaptchaToken.value);
    }

    const response = await fetch(Urls["portal"](), {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.code === "captcha_required") {
        // Trigger client-side CAPTCHA flow
        magicLinkCaptchaRequired.value = true;
        magicLinkMessage.value = "";
        magicLinkStatus.value = "idle";
        return;
      }
      // If we submitted with a CAPTCHA token and still failed, reset CAPTCHA state
      // so the error message can be displayed
      if (magicLinkCaptchaToken.value) {
        magicLinkCaptchaRequired.value = false;
        magicLinkCaptchaToken.value = "";
      }
      magicLinkStatus.value = "error";
      magicLinkMessage.value = data.error || "Failed to send sign-in link";
      return;
    }

    magicLinkStatus.value = "success";
    magicLinkMessage.value = "";
    magicLinkCaptchaRequired.value = false;
    magicLinkCaptchaToken.value = "";
    // Don't update emailAddress here - wait for authentication to be confirmed
    // The emailAddress will be updated automatically when checkSubscriber detects
    // the authenticated subscriber

    // Start polling for authentication
    magicLinkPollingInterval.value = setInterval(async () => {
      // Trigger checkSubscriber to see if user has authenticated
      props.checkSubscriber(new MouseEvent("click"));

      // Check if subscriber is now premium
      if (
        props.subscriber &&
        (props.subscriber.type === "premium" ||
          props.subscriber.type === "gifted")
      ) {
        // Clear interval and reload page
        if (magicLinkPollingInterval.value) {
          clearInterval(magicLinkPollingInterval.value);
          magicLinkPollingInterval.value = null;
        }
        window.location.reload();
      }
    }, 2000); // Poll every 2 seconds
  } catch {
    magicLinkStatus.value = "error";
    magicLinkMessage.value = "An error occurred. Please try again.";
  }
};

// Clean up polling interval on unmount
onUnmounted(() => {
  if (magicLinkPollingInterval.value) {
    clearInterval(magicLinkPollingInterval.value);
  }
});

// Watch for emailAddress changes to reset sign-in link form state
// Only reset if we're not in a sign-in link success state
watch(
  () => props.emailAddress,
  (newEmailAddress) => {
    if (newEmailAddress && magicLinkStatus.value !== "success") {
      showEmailInput.value = false;
      magicLinkStatus.value = "idle";
      magicLinkMessage.value = "";
    }
  }
);

// Watch for showEmailInput to autofocus the input when it becomes visible
watch(showEmailInput, async (newValue) => {
  if (newValue) {
    await nextTick();
    magicEmailInput.value?.focus();
  }
});

// Watch for subscriber changes to reload when authenticated
watch(
  () => props.subscriber,
  (newSubscriber) => {
    if (
      magicLinkStatus.value === "success" &&
      newSubscriber &&
      (newSubscriber.type === "premium" || newSubscriber.type === "gifted")
    ) {
      if (magicLinkPollingInterval.value) {
        clearInterval(magicLinkPollingInterval.value);
        magicLinkPollingInterval.value = null;
      }
      window.location.reload();
    }
  }
);

// Turnstile is only registered for Buttondown's own domains, not custom domains.
const isOnCustomDomain = computed(
  () =>
    props.newsletter.domain !== "" &&
    window.location.hostname === props.newsletter.domain
);

const AUTOCOMPLETE_ATTRIBUTES = {
  name: "name",
  first_name: "given-name",
  last_name: "family-name",
} as Record<string, string>;

const STATE_TO_LABEL = {
  paused:
    "You've paused your subscription, but you can resume it if you'd like!",
  premium: "You're now logged in as a premium subscriber of {newsletter.name}!",
  gifted:
    "The author of {newsletter.name} has gifted you a free premium subscription.",
  unsubscribed: "You unsubscribed from {newsletter.name}.",
  past_due: "Your payment to {newsletter.name} is past due!",
  churned:
    "Your premium subscription to {newsletter.name} has expired, but you can upgrade again if you'd like.",
  churning:
    "Your premium subscription to {newsletter.name} will expire at the end of the current billing cycle.",
} as Record<string, string>;

// Check for confirmed_subscription query parameter on mount
onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("state") === "confirmed_subscription") {
    showConfirmedDialog.value = true;
    // Remove the query parameter from URL without reloading
    urlParams.delete("state");
    const newUrl =
      window.location.pathname +
      (urlParams.toString() ? `?${urlParams.toString()}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }
});

const closeConfirmedDialog = () => {
  showConfirmedDialog.value = false;
};
</script>
